import React, { useState, useCallback } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material'

export type ConfirmDialogSeverity = 'error' | 'warning' | 'info' | 'primary'

export interface ConfirmDialogProps {
  /** Whether the dialog is open */
  open: boolean
  /** Called when the dialog should close (Cancel or backdrop). Not called while the confirm action is in progress. */
  onClose: () => void
  /** Dialog title (e.g. "Delete Content") */
  title: string
  /** Main message. Can be a string or React node. */
  description: React.ReactNode
  /** Async action when user confirms. On throw we show error and keep dialog open. */
  onConfirm: () => Promise<void>
  /** Confirm button label when idle (default: "Confirm") */
  confirmLabel?: string
  /** Confirm button label while action is in progress (default: "Loading...") */
  busyLabel?: string
  /** Cancel button label (default: "Cancel") */
  cancelLabel?: string
  /** Confirm button color / severity (default: "primary") */
  severity?: ConfirmDialogSeverity
  /** Optional id prefix for aria attributes (title id, description id) */
  ariaIdPrefix?: string
}

/**
 * Reusable confirmation dialog for any async action.
 * Manages loading and error state internally. Use for delete, unpublish, or other "are you sure?" flows.
 */
function ConfirmDialog ({
  open,
  onClose,
  title,
  description,
  onConfirm,
  confirmLabel = 'Confirm',
  busyLabel = 'Loading...',
  cancelLabel = 'Cancel',
  severity = 'primary',
  ariaIdPrefix = 'confirm-dialog',
}: ConfirmDialogProps) {
  const [isBusy, setIsBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const titleId = `${ariaIdPrefix}-title`
  const descriptionId = `${ariaIdPrefix}-description`

  const handleClose = useCallback(() => {
    if (!isBusy) {
      setError(null)
      onClose()
    }
  }, [isBusy, onClose])

  const handleConfirm = useCallback(async () => {
    try {
      setError(null)
      setIsBusy(true)
      await onConfirm()
      // On success caller may redirect or close; we don't close here
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
      setIsBusy(false)
    }
  }, [onConfirm])

  const confirmColor = severity === 'primary' ? 'primary' : severity

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
    >
      <DialogTitle id={titleId}>{title}</DialogTitle>
      <DialogContent>
        {typeof description === 'string' ? (
          <DialogContentText id={descriptionId}>{description}</DialogContentText>
        ) : (
          <div id={descriptionId}>{description}</div>
        )}
        {error != null && (
          <Box sx={{ mt: 2 }}>
            <DialogContentText color="error">{error}</DialogContentText>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isBusy}>
          {cancelLabel}
        </Button>
        <Button
          onClick={handleConfirm}
          color={confirmColor}
          variant="contained"
          disabled={isBusy}
          autoFocus
        >
          {isBusy ? busyLabel : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ConfirmDialog
